import React, { useState } from 'react'
import { Text, Box, Stack, Button, Badge, Icon, SimpleModal, SimpleCarousel } from '@/components/lib'
import QueryRaiser from './QueryRaiser'
import { FaExclamationTriangle, FaChartLine, FaInfoCircle, FaSearch } from 'react-icons/fa'

export const INSIGHT_CONFIG = {
  warning: {
    high: { colorPalette: 'red', borderColor: 'red.500', icon: FaExclamationTriangle },
    medium: { colorPalette: 'orange', borderColor: 'orange.400', icon: FaExclamationTriangle },
    low: { colorPalette: 'yellow', borderColor: 'yellow.400', icon: FaExclamationTriangle }
  },
  trend: {
    high: { colorPalette: 'blue', borderColor: 'blue.500', icon: FaChartLine },
    medium: { colorPalette: 'blue', borderColor: 'blue.400', icon: FaChartLine },
    low: { colorPalette: 'blue', borderColor: 'blue.300', icon: FaChartLine }
  },
  info: {
    high: { colorPalette: 'teal', borderColor: 'teal.500', icon: FaInfoCircle },
    medium: { colorPalette: 'teal', borderColor: 'teal.400', icon: FaInfoCircle },
    low: { colorPalette: 'gray', borderColor: 'gray.400', icon: FaInfoCircle }
  }
}

export function insightConfig (type, severity) {
  return (INSIGHT_CONFIG[type] || INSIGHT_CONFIG.info)[severity] ||
    (INSIGHT_CONFIG[type] || INSIGHT_CONFIG.info).medium
}

export function InsightCard ({ insight, onClick }) {
  const config = insightConfig(insight.type, insight.severity)
  return (
    <Box
      minW='210px'
      maxW='210px'
      borderWidth='1px'
      borderLeftWidth='3px'
      borderLeftColor={config.borderColor}
      rounded='md'
      bg='bg.surface'
      p={3}
      shadow='sm'
      cursor='pointer'
      _hover={{ shadow: 'md', borderColor: config.borderColor }}
      onClick={onClick}
    >
      <Stack gap={1.5}>
        <Stack direction='row' alignItems='center' gap={1.5}>
          <Icon as={config.icon} fontSize='xs' color={config.borderColor} />
          <Badge size='xs' colorPalette={config.colorPalette} variant='subtle' textTransform='capitalize'>
            {insight.severity} {insight.type}
          </Badge>
        </Stack>
        <Text fontSize='xs' fontWeight='semibold' lineClamp={2}>{insight.title}</Text>
        <Text fontSize='xs' color='fg.muted' lineClamp={3}>{insight.description}</Text>
      </Stack>
    </Box>
  )
}

export function InsightCarousel ({ insights, trialId, participantId, trialUsers }) {
  const [selectedInsight, setSelectedInsight] = useState(null)
  return (
    <>
      <SimpleCarousel
        gap={3} itemWidth='210px' items={
        insights.map((insight, i) => (
          <InsightCard key={i} insight={insight} onClick={() => setSelectedInsight(insight)} />
        ))
}
      />
      <InsightDetailModal
        insight={selectedInsight}
        trialId={trialId}
        participantId={participantId}
        trialUsers={trialUsers}
        isOpen={!!selectedInsight}
        onClose={() => setSelectedInsight(null)}
      />
    </>
  )
}

export function InsightDetailModal ({ insight, trialId, participantId, trialUsers, isOpen, onClose }) {
  if (!insight) return null
  const config = insightConfig(insight.type, insight.severity)
  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Stack direction='row' alignItems='center' gap={2}>
          <Icon as={config.icon} color={config.borderColor} />
          <Text fontWeight='semibold'>{insight.title}</Text>
        </Stack>
      }
      body={
        <Stack gap={4}>
          <Stack direction='row' alignItems='center' gap={2}>
            <Badge colorPalette={config.colorPalette} variant='subtle' textTransform='capitalize'>
              {insight.severity} {insight.type}
            </Badge>
          </Stack>
          <Text fontSize='sm'>{insight.description}</Text>

          {insight.suggestedDataTypeIdForQuery && (
            <Box pt={2} borderTopWidth='1px'>
              <Stack gap={2}>
                <Text fontSize='xs' color='fg.muted'>A query can be raised against the relevant data field for review.</Text>
                <QueryRaiser
                  trialId={trialId}
                  participantId={participantId}
                  dataTypeId={insight.suggestedDataTypeIdForQuery}
                  responseId={insight.suggestedResponseIdForQuery}
                  defaultTitle={insight.title}
                  defaultDescription={insight.description}
                  allowedAssignees={trialUsers}
                  trigger={
                    <Button size='sm' colorPalette='primary' variant='outline'>
                      <Icon as={FaSearch} mr={2} /> Raise a query
                    </Button>
                  }
                  onRaised={() => onClose()}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end'>
          <Button onClick={onClose}>Close</Button>
        </Stack>
      }
    />
  )
}
